import React from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { Button, Typography } from "@material-ui/core";
import ReactResizeDetector from "react-resize-detector";

import AuthStore from "stores/AuthStore";

import BackgroundLight from "components/Layout/Background/BackgroundLight";
import FormControl from "components/FormControl";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import ROUTES from "navigation/routes";

import { firebaseDynamicLinkUrl } from "utils/env";
import { correctTextFromQueryParams } from "utils/queryParamsUtils";

import {
  Container,
  Content,
  Logo,
  InfoContentContainer,
  InfoContent,
  Row,
  PasswordFieldControl,
  ButtonWrapper,
  Paper,
} from "./styles";

type Props = RouteComponentProps & SnackbarContextProps & {};

const PASSWORD_REGEX = /\d|[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/;

@observer
class ForgotPassword extends React.Component<Props> {
  @observable linkValidationError: string = "";
  @observable passwordValidationError: string = "";
  @observable passwordIsChanged: boolean = false;
  @observable height: number = window.innerHeight;
  @observable isLandscapeMode: boolean = false;

  get userId(): number | null {
    const userId = new URLSearchParams(this.props.location.search).get(
      "userId",
    );
    return userId ? Number.parseInt(userId) : null;
  }

  get code(): string | null {
    const code =
      new URLSearchParams(this.props.location.search).get("code") ?? null;

    return code ? correctTextFromQueryParams(code) : code;
  }

  get isMobile(): boolean {
    const isMobile =
      new URLSearchParams(this.props.location.search).get("isMobile") ?? null;

    return isMobile === "true";
  }

  async componentDidMount() {
    if (!this.isMobile) {
      this.props.history.push(ROUTES.LOGIN);

      return;
    }

    try {
      await AuthStore.preChangePasswordValidate({
        userId: this.userId,
        code: this.code,
      });
    } catch (error) {
      this.linkValidationError = error?.message;
    }
  }

  validate = ({
    newPassword,
    confirmPassword,
  }: {
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (newPassword !== confirmPassword) {
      this.passwordValidationError =
        "Password and confirmation password do not match.";
      return false;
    }

    if (newPassword?.length < 8 || !PASSWORD_REGEX.test(newPassword)) {
      this.passwordValidationError =
        "Your password should be minimum 8 characters long and include at least one digit or special symbol.";
      return false;
    }

    return true;
  };

  resetPasswordValidationError = () => {
    this.passwordValidationError = "";
  };

  onChangePassword = async (values: any) => {
    const { setMessage } = this.props;

    this.resetPasswordValidationError();

    const isValid = this.validate(values);
    if (!isValid) {
      return;
    }

    try {
      await AuthStore.changePassword({
        userId: this.userId,
        code: this.code,
        newPassword: values.newPassword,
      });

      this.passwordIsChanged = true;
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onLoginClick = () => {
    window.open(firebaseDynamicLinkUrl, "_self");
  };

  onResize = () => {
    this.height = window.innerHeight;
    this.isLandscapeMode = window.innerWidth > window.innerHeight;
  };

  render() {
    return (
      <ReactResizeDetector
        onResize={this.onResize}
        refreshMode={"debounce"}
        refreshRate={200}
      >
        <Container windowHeight={this.height}>
          <BackgroundLight />
          <Paper windowHeight={this.height}>
            <Content isLandscapeMode={this.isLandscapeMode}>
              <Logo />

              {this.passwordIsChanged ? (
                <InfoContentContainer>
                  <InfoContent>
                    <Row isTextRow>
                      <Typography variant="subtitle2">
                        Your new password is successfully saved. Please sign in
                        via the mobile app.
                      </Typography>
                    </Row>
                  </InfoContent>
                  <ButtonWrapper>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={this.onLoginClick}
                      fullWidth
                    >
                      Login
                    </Button>
                  </ButtonWrapper>
                </InfoContentContainer>
              ) : (
                <FormControl onSubmit={this.onChangePassword}>
                  {this.linkValidationError ? (
                    <Row isTextRow>
                      <Typography variant="subtitle2">
                        {this.linkValidationError}
                      </Typography>
                    </Row>
                  ) : (
                    <InfoContentContainer>
                      <InfoContent>
                        <Row isTextRow>
                          <Typography variant="subtitle2">
                            Create a new password to log in to your account in
                            the mobile app:
                          </Typography>
                        </Row>

                        <Row>
                          <PasswordFieldControl
                            variant="outlined"
                            margin="none"
                            required
                            fullWidth
                            label="New password"
                            name="newPassword"
                            type="password"
                            error={this.passwordValidationError}
                          />
                        </Row>

                        <Row>
                          <PasswordFieldControl
                            variant="outlined"
                            margin="none"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm password"
                            type="password"
                            autoComplete="current-password"
                            error={this.passwordValidationError}
                          />
                        </Row>
                      </InfoContent>
                      <ButtonWrapper>
                        <SubmitButtonControl render={this.renderButton} />
                      </ButtonWrapper>
                    </InfoContentContainer>
                  )}
                </FormControl>
              )}
            </Content>
          </Paper>
        </Container>
      </ReactResizeDetector>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      fullWidth
      variant="contained"
      color="primary"
      isLoading={AuthStore.isLoading}
    >
      Save
    </LoadingButton>
  );
}

export default withRouter(withGlobalSnackbar(ForgotPassword));
